'use client';

/**
 * The site's header: a floating pill that tightens as the page moves.
 *
 * At the top of a page it is a wide pill with room to breathe; the moment the
 * page scrolls it draws in horizontally — the wordmark folds down to the mark,
 * the account link steps back — so it stays present without sitting on the
 * content it is floating over. It moves on the house spring, as one object:
 * the pill's width, its padding and what it holds all change together.
 *
 * It wears the page's theme and never one of its own: every surface it floats
 * over is in that theme too (the homepage's stages included), so the glass
 * always sits on its own palette.
 *
 * Only THIS wrapper is a client component. Everything inside it is rendered by
 * the (static, cookie-free) server header and reads the state through the
 * `group/header` data attribute, so no public page is opted into per-request
 * rendering by its header (see LandingNav.tsx).
 *
 * In flow, not fixed: the wrapper is `sticky` and keeps its height, so every
 * page that already laid itself out under a 56px bar still does.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';

/** Scroll distance after which the pill draws in. Enough that a trackpad's
 *  settle at the very top never toggles it. */
const COMPACT_AFTER_PX = 24;

export default function FloatingHeader({ children }: { children: ReactNode }) {
  const [compact, setCompact] = useState(false);
  const frame = useRef<number | null>(null);

  useEffect(() => {
    const read = () => {
      frame.current = null;
      const next = window.scrollY > COMPACT_AFTER_PX;
      setCompact((prev) => (prev === next ? prev : next));
    };
    const onScroll = () => {
      if (frame.current === null) frame.current = requestAnimationFrame(read);
    };
    read();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame.current !== null) cancelAnimationFrame(frame.current);
    };
  }, []);

  return (
    <div className="pointer-events-none sticky top-0 z-50 flex justify-center px-3 pt-3 sm:px-4">
      <header
        data-testid="site-header"
        data-compact={compact}
        // The width and its spring live in `.rt-header-pill` (globals.css):
        // the drawn-in measure is a min() of two lengths, which a Tailwind
        // arbitrary value cannot carry through this project's scanner.
        className="group/header rt-glass rt-header-pill pointer-events-auto flex h-14 w-full items-center gap-2 rounded-full pl-4 pr-2 text-[var(--rt-ink)] sm:gap-3 sm:pl-5"
      >
        {children}
      </header>
    </div>
  );
}
